import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { pickBy } from 'lodash-es';

import {
  setSelectedForm, signUp,
} from '../actions';
import {
  FORMS__SIGN_IN,
} from '../constants';
import initialEditorstate from './initialEditorState';
import useGetOrganizations from './hooks/getOrganizationsHook';
import validate from './editorStateValidator';
import { getValidationError } from '../../helpers/dataOperations/form/validationMessageHelper';
import RecaptchaVerification from '../../shared/components/recaptchaVerification';
import FormNotification from '../../shared/components/formNotification';

const SignUp = ({ setSelectedFormParams }) => {
  const [submitButtonDisabled, setSubmitButtonDisabled] = useState(true);
  const [isDataSaving, setIsDataSaving] = useState(false);
  const [validationMessage, setValidationMessage] = useState(true);
  const [globalErrorMessage, setGlobalErrorMessage] = useState('');
  const [editorState, setEditorState] = useState(initialEditorstate);
  const { organizations } = useGetOrganizations();
  const dispatch = useDispatch();
  const recaptchaRef = useRef(null);

  const returnButtonClick = () => {
    dispatch(setSelectedForm(FORMS__SIGN_IN));
  };

  const prepareRequestData = () => {
    return pickBy(editorState, (value, key) => (!!value && key !== 'passwordConfirm'));
  };

  const onSignupSuccess = () => {
    setSelectedFormParams({ registerSuccessAccountName: editorState.email });
    setIsDataSaving(false);
    dispatch(setSelectedForm(FORMS__SIGN_IN));
  };

  const signUpFailure = (message) => {
    if (message) {
      setGlobalErrorMessage(message);
    }

    setIsDataSaving(false);
  };

  const signUpButtonClick = () => {
    setIsDataSaving(true);
    const errors = validate(editorState);

    if (!errors) {
      const requestData = prepareRequestData();
      setValidationMessage();
      signUp(requestData, onSignupSuccess, signUpFailure);
    } else {
      setValidationMessage(errors);
      setIsDataSaving(false);
    }
  };

  const onRecaptchaVerify = (isVerificationSuccess) => {
    if (isVerificationSuccess) {
      setSubmitButtonDisabled(false);
    } else {
      setSubmitButtonDisabled(true);
    }
  };

  const onChange = (name) => (evt) => {
    setEditorState({ ...editorState, [name]: evt.target.value });
  };

  let submitButtonClassNames = 'button sign-up-button';
  if (submitButtonDisabled || isDataSaving) submitButtonClassNames += ' disabled';
  const selectOrganizationsData = organizations.map((organization) => (
    <option key={organization.id} value={organization.id}>{organization.name}</option>
  ));

  return (
    <div className="sign-up">
      <div className="title">
        Zarejestruj się
      </div>
      <div className="description">
        Wypełnij prosty formularz i dołącz do użytkowników portalu.
      </div>
      <div className="form">
        <div className="form-group">
          <div className="error-message">
            {getValidationError(validationMessage, 'firstName')}
          </div>
          <label htmlFor="firstname">
            <div className="required-label-field">
              Imię
            </div>
            <input
              className="form-control"
              id="firstname"
              type="text"
              onChange={onChange('firstname')}
            />
          </label>
        </div>
        <div className="form-group">
          <div className="error-message">
            {getValidationError(validationMessage, 'lastName')}
          </div>
          <label htmlFor="lastName">
            <div className="required-label-field">
              Nazwisko
            </div>
            <input
              className="form-control"
              id="lastName"
              type="text"
              onChange={onChange('lastName')}
            />
          </label>
        </div>
        <div className="form-group">
          <div className="error-message">
            {getValidationError(validationMessage, 'organizationId')}
          </div>
          <label htmlFor="community">
            <div className="required-label-field">
              Organizacja
            </div>
            <select
              className="form-control"
              id="community"
              label="Organizacja"
              onChange={onChange('organizationId')}
              defaultValue="defaultSelection"
            >
              <option key="defaultSelection" disabled value="defaultSelection">-- Wybierz --</option>
              {selectOrganizationsData}
            </select>
          </label>
        </div>
        <div className="form-group">
          <div className="error-message">
            {getValidationError(validationMessage, 'email')}
          </div>
          <label htmlFor="email">
            <div className="required-label-field">
              E-mail
            </div>
            <input
              className="form-control"
              id="email"
              type="email"
              onChange={onChange('email')}
            />
          </label>
        </div>
        <div className="form-group">
          <div className="error-message">
            {getValidationError(validationMessage, 'password')}
          </div>
          <label htmlFor="password1">
            <div className="required-label-field">
              Hasło
            </div>
            <input
              className="form-control"
              id="password1"
              type="password"
              onChange={onChange('password')}
            />
          </label>
        </div>
        <div className="form-group">
          <div className="error-message">
            {getValidationError(validationMessage, 'passwordConfirm')}
          </div>
          <label htmlFor="password2">
            <div className="required-label-field">
              Powtórz hasło
            </div>
            <input
              className="form-control"
              id="password2"
              type="password"
              onChange={onChange('passwordConfirm')}
            />
          </label>
        </div>
        <div className="form-group">
          <div className="error-message">
            {getValidationError(validationMessage, 'phone')}
          </div>
          <label htmlFor="phone">
            Telefon
            <input
              className="form-control"
              id="phone"
              type="text"
              onChange={onChange('phone')}
            />
          </label>
        </div>
        <div className="form-group">
          <div className="error-message">
            {getValidationError(validationMessage, 'mobilephone')}
          </div>
          <label htmlFor="mobilephone">
            Telefon komórkowy
            <input
              className="form-control"
              id="mobilephone"
              type="text"
              onChange={onChange('mobilePhone')}
            />
          </label>
        </div>
        <RecaptchaVerification
          className="form-group"
          onRecaptchaVerify={onRecaptchaVerify}
          recaptchaRef={recaptchaRef}
        />
      </div>
      <FormNotification message={globalErrorMessage} />
      <div className="actions">
        <Choose>
          <When condition={isDataSaving}>
            <div className="button sign-up-button spinner-wrapper">
              <div className="spinner-border text-light" role="status" />
            </div>
          </When>
          <Otherwise>
            <div
              className={submitButtonClassNames}
              onClick={signUpButtonClick}
              role="button"
              title="Załóż konto"
            >
              Załóż konto
            </div>
          </Otherwise>
        </Choose>
        <div
          className="button return-button float-right"
          onClick={returnButtonClick}
          role="button"
          title="Powrót"
        >
          Powrót
        </div>
      </div>
    </div>
  );
};

SignUp.propTypes = {
  setSelectedFormParams: PropTypes.func.isRequired,
};

export default SignUp;
